<template>
    <div
        :class="[
            'plan-container',
            { enterprise: isEnterprisePlan },
            { 'plan-active': plan.id === currentPlan?.id },
        ]"
    >
        <div class="plan-header">
            <ColorTag
                :title="plan.name.replace('Yearly ', '')"
                :color="currentPlan?.id === plan.id ? 'green' : 'blue'"
                content=""
                style="display: inline-block"
            />
            <Spacer height="1rem" />
            <Text v-if="isEnterprisePlan" as="span" size="f-3" weight="600">Custom</Text>
            <Text v-else as="span" size="f-3" weight="600">${{ plan.price }}</Text>
            <Text v-if="!isEnterprisePlan" as="span" size="f-5" weight="500"
                >/{{ plan.period_interval }}
            </Text>
        </div>
        <div class="plan-items">
            <div class="plan-item">
                <PricingCheck :hex="currentPlan?.id === plan.id ? '#00A861' : '#006dff'" />
                <Spacer width="0.75rem" height="0.25rem" />
                <Text as="span" size="f-8" v-if="plan.account_limit">
                    <b>{{ plan.account_limit }}</b> Accounts
                </Text>
            </div>
            <div class="plan-item">
                <PricingCheck :hex="currentPlan?.id === plan.id ? '#00A861' : '#006dff'" />
                <Spacer width="0.75rem" height="0.25rem" />
                <Text v-if="isEnterprisePlan" as="span" size="f-8"> <b>Custom</b> Spend/mo</Text>
                <Text v-else as="span" size="f-8">
                    <b>{{ formatPrice(plan.spend_limit) }}</b> Spend/mo
                </Text>
            </div>
            <div class="plan-item">
                <PricingCheck :hex="currentPlan?.id === plan.id ? '#00A861' : '#006dff'" />
                <Spacer width="0.75rem" height="0.25rem" />
                <Text as="span" size="f-8" v-if="plan.id.includes(Billing.StandardPlan.Enterprise)">
                    Account Manager
                </Text>
                <Text
                    as="span"
                    size="f-8"
                    v-else-if="!plan.id.includes(Billing.StandardPlan.Basic)"
                >
                    Priority Support
                </Text>
                <Text as="span" size="f-8" v-else>Live Chat Support</Text>
            </div>
            <div class="plan-item">
                <PricingCheck :hex="currentPlan?.id === plan.id ? '#00A861' : '#006dff'" />
                <Spacer width="0.75rem" height="0.25rem" />
                <Text as="span" size="f-8">
                    <b v-if="plan.id.includes(Billing.StandardPlan.Enterprise)">6hr</b>
                    <b v-else-if="!plan.id.includes(Billing.StandardPlan.Basic)">12hr</b>
                    <b v-else>24hr</b> Refresh Cycle
                </Text>
            </div>
            <div class="plan-item">
                <svg
                    v-if="plan.id.includes(Billing.StandardPlan.Basic)"
                    width="18"
                    height="18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <circle cx="9" cy="9" r="9" fill="#E0E0E5" />
                    <path
                        d="m11.5 6.5-5 5M6.5 6.5l5 5"
                        stroke="#fff"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    />
                </svg>
                <PricingCheck :hex="currentPlan?.id === plan.id ? '#00A861' : '#006dff'" v-else />
                <Spacer width="0.75rem" height="0.25rem" />
                <Text as="span" size="f-8">Google Ads Reports</Text>
            </div>
        </div>
        <div class="plan-button">
            <!-- Confirmation state -->
            <template v-if="confirmingPlan">
                <Text as="p" size="f-8" align="center" weight="600" style="margin-bottom: 0.75rem">
                    Due today: {{ formatPrice(previewAmount) }}
                </Text>
                <oButton
                    @clicked="confirmPlanChange"
                    :loading="updatingPlan"
                    color="blue"
                    size="medium"
                    class="w-100"
                >
                    Confirm
                </oButton>
            </template>
            <!-- Default state -->
            <template v-else>
                <oButton
                    ref="selectPlanButton"
                    @clicked="previewPlanChange"
                    :loading="updatingPlan"
                    :success="plan.id === currentPlan?.id"
                    :color="plan.id === currentPlan?.id ? 'green' : 'blue'"
                    size="medium"
                    class="w-100"
                >
                    <span v-if="isEnterprisePlan">Book a Call</span>
                    <span v-else>Select Plan</span>
                    <template #icon>
                        <svg
                            width="12"
                            height="12"
                            viewBox="0 0 12 12"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                stroke="#fff"
                                d="M1 11 11 1m0 0H4m7 0v7"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                    </template>
                </oButton>
            </template>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { authRequest, Endpoint } from '@/composition/api/useAPI'
import { computed, PropType, ref, watch } from 'vue'
import { Api, Billing } from '@opteo/types'
import { loadStripe } from '@stripe/stripe-js'
import { STRIPE_PUBLISHABLE_KEY } from '@/lib/env'
import { useUser } from '@/composition/user/useUser'
import { useSubscription } from '@/composition/billing/useSubscription'
import { usePlanSelectionModal } from '@/composition/billing/usePlanSelectionModal'
import {
    oButton,
    ColorTag,
    Text,
    Spacer,
    CheckIcon,
    useMoney,
    showSnackbar,
} from '@opteo/components-next'
import PricingCheck from '@/components/global/PricingCheck.vue'

const props = defineProps({
    plan: {
        type: Object as PropType<Billing.CurrentPlan>,
        required: true,
    },
})

const { currentPlan, mutateUserInfo, totalSpend30d } = useUser()
const {
    mutate: mutateSubscription,
    mutateCoupon,
    mutateUpcomingInvoiceAmount,
    isTrialing,
    latestSubscription,
} = useSubscription()
const { showPlanSelectionModal } = usePlanSelectionModal()

const planID = computed(() => props.plan.id)

const updatingPlan = ref(false)
const confirmingPlan = ref(false)
const previewAmount = ref<number | null>(null)

watch(showPlanSelectionModal, open => {
    if (!open) confirmingPlan.value = false
})

const selectPlanButton = ref()

const isEnterprisePlan = computed(() =>
    [
        Billing.StandardPlan.Enterprise,
        Billing.StandardPlan.YearlyEnterprise,
        Billing.LegacyPlan.Enterprise,
        Billing.LegacyPlan.YearlyEnterprise,
    ].includes(props.plan.id as Billing.StandardPlan)
)

async function previewPlanChange() {
    if (updatingPlan.value || confirmingPlan.value || planID.value === currentPlan.value?.id) {
        return
    }

    if (isEnterprisePlan.value) {
        window
            .open('https://calendly.com/adamdale/opteo-enterprise-plan-upgrade', '_blank')
            ?.focus()
        return
    }

    if (isTrialing.value || !latestSubscription.value) {
        await confirmPlanChange()
        return
    }

    updatingPlan.value = true

    try {
        const result = await authRequest<{ amount_due: number }>(
            Endpoint.GetStripePlanChangePreview,
            { body: { new_plan_id: planID.value } }
        )
        previewAmount.value = result.amount_due
        confirmingPlan.value = true
    } catch (err) {
        showSnackbar({ message: 'Could not preview plan change: ' + (err as Error).message })
    } finally {
        updatingPlan.value = false
    }
}

async function confirmPlanChange() {
    updatingPlan.value = true

    try {
        const result = await authRequest<Api.UpdateStripePlan.Response>(Endpoint.UpdateStripePlan, {
            body: { new_plan_id: planID.value },
        })

        if (result.status === 'requires_action') {
            const stripe = await loadStripe(STRIPE_PUBLISHABLE_KEY)
            if (!stripe) throw new Error('Stripe failed to load')
            const { error } = await stripe.confirmCardPayment(result.client_secret)
            if (error) throw new Error(error.message)
        }

        const overLimit = props.plan.spend_limit
            ? totalSpend30d.value > props.plan.spend_limit * 1.05
            : false

        mutateUserInfo({ current_plan: props.plan, over_limit: overLimit })

        mutateSubscription()
        mutateCoupon()
        mutateUpcomingInvoiceAmount()

        confirmingPlan.value = false
    } catch (err) {
        showSnackbar({
            message: 'There was an error updating your plan: ' + (err as Error).message,
        })
        confirmingPlan.value = false
        showPlanSelectionModal.value = false
        mutateSubscription()
    } finally {
        updatingPlan.value = false
    }
}

/**
 * @description Takes a raw number and formats it with a leading '$' sign.
 * @example
 * const formattedPrice = formatPrice(25_000)
 * console.log(formattedPrice) // Expected result: '$25,000'
 * @param {number | null} rawPrice
 * Note: `rawPrice` should never be null since we're sending info from existing plans, which have a defined number value.
 * The `Billing.CurrentPlan` type includes `null`s for old plans compatibility reasons.
 * @returns A formatted price (or '$0' if `null`)
 */
const formatPrice = (rawPrice: number | null) => {
    return useMoney({ value: rawPrice ?? 0, currency: 'USD', compact: false }).displayValue.value
}
</script>

<style lang="scss" scoped>
@import '@/assets/css/theme.scss';
@import '@/assets/css/variables.scss';

.plan-container {
    @include container;
    @include br-24;
    width: 15rem;
}

.plan-header {
    @include pa-24;
    border-bottom: 1px solid;
    @include opteo-border;
}

.plan-container.enterprise .plan-header {
    border-color: rgba(0, 3, 82, 0.03);
}

.plan-items {
    @include pa-24;
    border-bottom: 1px solid;
    @include opteo-border;
}

.plan-container.enterprise .plan-items {
    border-color: rgba(0, 3, 82, 0.03);
}

.plan-item {
    @include flex;
    @include items-center;
    @include mb-12;
}
.plan-item:last-child {
    @include mb-0;
}

.plan-button {
    @include ph-24;
    @include pv-20;
}
.plan-button :deep(.o-button span) {
    width: 100%;
}

// Enterprise Plan
.plan-container.enterprise {
    background: rgba(0, 43, 214, 0.05);
    box-shadow:
        0 6px 6px -3px rgba(0, 0, 0, 0.02),
        0 4px 4px -2px rgba(0, 0, 0, 0.03),
        0 2px 2px -1px rgba(0, 0, 0, 0.05),
        0 1px 1px -0.5px rgba(0, 0, 0, 0.05),
        0 0 0 1px #ebedf8 !important;
    border-radius: 1.75rem;
    color: #0007d6 !important;
}
.plan-container.enterprise span.black {
    color: #0007d6 !important;
}
.plan-container.enterprise :deep(circle) {
    fill: #0007d6 !important;
}
.plan-container.enterprise :deep(.o-button) {
    background: #0007d6 !important;
}
</style>
